import type React from "react";
import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Fixation, Saccade } from "../../types/gazeHeatmapTypes";

interface ScanpathCanvasProps {
  /** Array of fixations to render */
  fixations: Fixation[];
  /** Array of saccades to render */
  saccades: Saccade[];
  /** Current fixation (highlighted) */
  currentFixation?: Fixation | null;
  /** Canvas width (matches parent container) */
  width?: number;
  /** Canvas height (matches parent container) */
  height?: number;
  /** Color for fixation points */
  fixationColor?: string;
  /** Color for saccade lines */
  saccadeColor?: string;
  /** Color for current fixation highlight */
  currentFixationColor?: string;
  /** Base size for fixation circles (scaled by duration) */
  fixationBaseSize?: number;
  /** Whether to show fixation numbers */
  showNumbers?: boolean;
  /** Whether to show saccade arrows */
  showArrows?: boolean;
  /** Opacity (0-1) */
  opacity?: number;
}

/**
 * Format time in seconds to a readable string
 */
function formatTime(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Format duration in ms to a human-readable string
 * - < 1000ms: show as "Xms"
 * - >= 1000ms and < 60s: show as "X.Xs"
 * - >= 60s: show as "Xm Xs"
 */
function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

/**
 * Canvas overlay for rendering scanpath (fixations and saccades) in 2D
 * Uses UV coordinates which map to the equirectangular projection
 */
const ScanpathCanvas: React.FC<ScanpathCanvasProps> = ({
  fixations,
  saccades,
  currentFixation,
  width = 2048,
  height = 1024,
  fixationColor = "#3b82f6", // blue-500
  saccadeColor = "#6b7280", // gray-500
  currentFixationColor = "#ef4444", // red-500
  fixationBaseSize = 12,
  showNumbers = true,
  showArrows = true,
  opacity = 1,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredFixationId, setHoveredFixationId] = useState<number | null>(null);

  // Helper to convert UV to percentage coordinates
  const uvToPercent = (u: number, v: number): { x: number; y: number } => ({
    x: u * 100,
    y: (1 - v) * 100, // Invert V for screen coordinates
  });

  // Draw saccades on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    if (fixations.length === 0) return;

    ctx.globalAlpha = opacity;

    // Helper to convert UV to canvas coordinates
    const uvToCanvas = (u: number, v: number): { x: number; y: number } => ({
      x: u * width,
      y: (1 - v) * height,
    });

    // Calculate the shortest path between two U coordinates (handling wrap-around)
    const shortestUPath = (u1: number, u2: number): { du: number; wrap: boolean } => {
      const direct = u2 - u1;
      const wrapRight = direct + 1;
      const wrapLeft = direct - 1;

      const absDirect = Math.abs(direct);
      const absWrapRight = Math.abs(wrapRight);
      const absWrapLeft = Math.abs(wrapLeft);

      if (absDirect <= absWrapRight && absDirect <= absWrapLeft) {
        return { du: direct, wrap: false };
      } else if (absWrapRight < absWrapLeft) {
        return { du: wrapRight, wrap: true };
      } else {
        return { du: wrapLeft, wrap: true };
      }
    };

    // Draw saccades (lines between fixations)
    ctx.strokeStyle = saccadeColor;
    ctx.lineWidth = 2;
    ctx.setLineDash([]);

    for (const saccade of saccades) {
      const from = fixations.find((f) => f.id === saccade.fromFixationId);
      const to = fixations.find((f) => f.id === saccade.toFixationId);

      if (!from || !to) continue;

      const start = uvToCanvas(from.centroidUV.u, from.centroidUV.v);
      const { du, wrap } = shortestUPath(from.centroidUV.u, to.centroidUV.u);

      if (wrap) {
        const midV = (from.centroidUV.v + to.centroidUV.v) / 2;

        if (du > 0) {
          const rightEdge = uvToCanvas(1, midV);
          const leftEdge = uvToCanvas(0, midV);
          const end = uvToCanvas(to.centroidUV.u, to.centroidUV.v);

          ctx.beginPath();
          ctx.moveTo(start.x, start.y);
          ctx.lineTo(rightEdge.x, rightEdge.y);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(leftEdge.x, leftEdge.y);
          ctx.lineTo(end.x, end.y);
          ctx.stroke();
        } else {
          const leftEdge = uvToCanvas(0, midV);
          const rightEdge = uvToCanvas(1, midV);
          const end = uvToCanvas(to.centroidUV.u, to.centroidUV.v);

          ctx.beginPath();
          ctx.moveTo(start.x, start.y);
          ctx.lineTo(leftEdge.x, leftEdge.y);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(rightEdge.x, rightEdge.y);
          ctx.lineTo(end.x, end.y);
          ctx.stroke();
        }
      } else {
        const end = uvToCanvas(to.centroidUV.u, to.centroidUV.v);

        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();

        if (showArrows) {
          const angle = Math.atan2(end.y - start.y, end.x - start.x);
          const arrowSize = 8;
          const arrowX = end.x - Math.cos(angle) * 15;
          const arrowY = end.y - Math.sin(angle) * 15;

          ctx.beginPath();
          ctx.moveTo(arrowX, arrowY);
          ctx.lineTo(
            arrowX - arrowSize * Math.cos(angle - Math.PI / 6),
            arrowY - arrowSize * Math.sin(angle - Math.PI / 6),
          );
          ctx.lineTo(
            arrowX - arrowSize * Math.cos(angle + Math.PI / 6),
            arrowY - arrowSize * Math.sin(angle + Math.PI / 6),
          );
          ctx.closePath();
          ctx.fillStyle = saccadeColor;
          ctx.fill();
        }
      }
    }

    ctx.globalAlpha = 1;
  }, [fixations, saccades, width, height, saccadeColor, showArrows, opacity]);

  return (
    <div className="absolute inset-0 w-full h-full">
      {/* Canvas for saccade lines */}
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ imageRendering: "crisp-edges" }}
      />

      {/* HTML overlay for fixations with hover */}
      <div className="absolute inset-0 w-full h-full" style={{ opacity }}>
        {fixations.map((fixation) => {
          const pos = uvToPercent(fixation.centroidUV.u, fixation.centroidUV.v);
          const isCurrent = currentFixation?.id === fixation.id;
          const isHovered = hoveredFixationId === fixation.id;
          const color = isCurrent ? currentFixationColor : fixationColor;

          // Scale circle size by duration
          const durationScale = Math.sqrt(fixation.duration / 100);
          const sizeMultiplier = Math.min(2, Math.max(0.5, durationScale));
          const pixelSize = Math.round(fixationBaseSize * 2 * sizeMultiplier);

          return (
            <div
              key={fixation.id}
              style={{
                position: "absolute",
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: `${pixelSize}px`,
                  height: `${pixelSize}px`,
                  cursor: "pointer",
                }}
                onMouseEnter={() => setHoveredFixationId(fixation.id)}
                onMouseLeave={() => setHoveredFixationId(null)}
              >
                {/* Circle background */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    backgroundColor: `${color}80`,
                    border: `${isCurrent ? 3 : 2}px solid ${color}`,
                    boxShadow:
                      isCurrent || isHovered
                        ? `0 0 0 3px ${color}40, 0 2px 6px rgba(0,0,0,0.4)`
                        : "0 2px 4px rgba(0,0,0,0.3)",
                    transform: isHovered ? "scale(1.15)" : "scale(1)",
                    transition: "transform 0.15s ease-out, box-shadow 0.15s ease-out",
                  }}
                />

                {/* Number label */}
                {showNumbers && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: `${Math.max(10, Math.round(pixelSize * 0.45))}px`,
                      fontWeight: "bold",
                      textShadow: "0 1px 2px rgba(0,0,0,0.8)",
                    }}
                  >
                    {fixation.id + 1}
                  </div>
                )}

                {/* Tooltip on hover */}
                {isHovered && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      marginBottom: "8px",
                      backgroundColor: "rgba(0, 0, 0, 0.9)",
                      color: "white",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                      zIndex: 1000,
                    }}
                  >
                    <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                      {t("statsNew.scanpath.fixation")} #{fixation.id + 1}
                    </div>
                    <div
                      style={{ display: "flex", flexDirection: "column", gap: "2px", opacity: 0.9 }}
                    >
                      <span>
                        ⏱ {t("statsNew.scanpath.startTime")}: {formatTime(fixation.startTime)}
                      </span>
                      <span>
                        ⏳ {t("statsNew.scanpath.duration")}: {formatDuration(fixation.duration)}
                      </span>
                      <span>
                        👁 {t("statsNew.scanpath.points")}: {fixation.pointCount}
                      </span>
                      <span>
                        💪 {t("statsNew.scanpath.strength")}: {Math.round(fixation.strength * 100)}%
                      </span>
                    </div>
                    {/* Tooltip arrow */}
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 0,
                        height: 0,
                        borderLeft: "6px solid transparent",
                        borderRight: "6px solid transparent",
                        borderTop: "6px solid rgba(0, 0, 0, 0.9)",
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScanpathCanvas;
